/**
 * Canvas Preview Test Page
 *
 * Renders a single SceneDocument blueprint via CanvasKitPreview at its native dimensions.
 * Used by Playwright/MCP for visual regression testing of blueprints.
 *
 * Usage from Playwright/MCP:
 *   page.evaluate(() => window.__loadBlueprint({ version: 2, width: 600, height: 400, nodes: {...}, rootChildren: [...] }))
 *   page.screenshot()
 *
 * This uses the REAL CanvasKitPreview component -- the same one used in
 * template galleries, inline section previews, and workspace unfocused slots.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Stack, Chip } from '@mui/material';
import { CanvasKitPreview } from '@/components/canvas-editor/CanvasKitPreview';
import type { SceneDocument } from '@/lib/scene-document';

export const Route = createFileRoute('/_authed/dashboard/test/canvas-preview')({
  component: CanvasPreviewTestPage,
});

function CanvasPreviewTestPage() {
  const [blueprint, setBlueprint] = useState<Record<string, any> | null>(null);
  const [label, setLabel] = useState('No blueprint loaded');
  const [status, setStatus] = useState<'idle' | 'loaded'>('idle');

  // Expose loadBlueprint on window for Playwright/MCP injection
  useEffect(() => {
    (window as any).__loadBlueprint = (json: Record<string, any>, name?: string) => {
      setBlueprint(json);
      setLabel(name || 'Blueprint');
      setStatus('loaded');
    };
    (window as any).__clearBlueprint = () => {
      setBlueprint(null);
      setLabel('No blueprint loaded');
      setStatus('idle');
    };
    return () => {
      delete (window as any).__loadBlueprint;
      delete (window as any).__clearBlueprint;
    };
  }, []);

  const width = blueprint?.width || 600;
  const height = blueprint?.height || 400;
  const sceneDoc = useMemo(
    () => (blueprint ? blueprint as SceneDocument : null),
    [blueprint],
  );

  return (
    <Box
      data-testid="canvas-preview-test-page"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        p: 4,
      }}
    >
      <Stack spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip label={`${width}x${height}`} size="small" variant="outlined" />
          <Chip
            label={status}
            size="small"
            color={status === 'loaded' ? 'success' : 'default'}
            variant="outlined"
            data-testid="preview-status"
          />
        </Stack>
      </Stack>

      <Box
        data-testid="canvas-preview-container"
        sx={{
          width: width,
          bgcolor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {sceneDoc ? (
          <CanvasKitPreview
            sceneDocument={sceneDoc}
            width={width}
            height={height}
            renderWidth={width}
          />
        ) : (
          <Box
            sx={{
              width: width,
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2">
              Call window.__loadBlueprint(json) to render a blueprint
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
